import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Copy, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { MpesaIcon } from '@/components/ui/mpesa-icon';
import { WhatsAppIcon } from '@/components/ui/whatsapp-icon';
import { useBranches } from '@/features/branches/hooks/use-branches';
import { useSettings } from '@/features/settings/hooks/use-settings';
import {
  getContactDisplayNumber,
  getContactWhatsappTarget,
} from '@/features/settings/support-contacts';

function formatAmount(value: number | null): string {
  return value && Number.isFinite(value) ? `KES ${value.toLocaleString()}` : 'Enter amount on your phone';
}

export default function MpesaPage() {
  const [searchParams] = useSearchParams();
  const { data: branches } = useBranches();
  const { settings } = useSettings();
  const [verificationCode, setVerificationCode] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [orderReference, setOrderReference] = useState(searchParams.get('orderId') || '');
  const [copied, setCopied] = useState(false);

  const mainBranch = branches?.find((branch) => branch.is_main) || branches?.[0];
  const tillNumber = searchParams.get('till') || mainBranch?.mpesa_shortcode || '3022071';
  const rawAmount = Number(searchParams.get('amount') || '');
  const amount = Number.isFinite(rawAmount) && rawAmount > 0 ? rawAmount : null;

  const supportContacts = settings.support_numbers || [];
  const defaultSupportContact =
    supportContacts.find((contact) => contact.isDefault) || supportContacts[0] || null;
  const whatsappTarget = getContactWhatsappTarget(defaultSupportContact);
  const supportPhone = defaultSupportContact?.phone || settings.support_phone || '';
  const displayPhone = getContactDisplayNumber(defaultSupportContact);

  const verificationMessage = useMemo(() => {
    const parts = [
      'Hello Karebe, I need M-Pesa payment verification.',
      `Till Number: ${tillNumber}`,
      `Code: ${verificationCode || 'Not provided'}`,
    ];

    if (amount) {
      parts.push(`Amount: KES ${amount.toLocaleString()}`);
    }

    if (customerPhone.trim()) {
      parts.push(`Customer Phone: ${customerPhone.trim()}`);
    }

    if (orderReference.trim()) {
      parts.push(`Order Reference: ${orderReference.trim()}`);
    }

    return parts.join('\n');
  }, [amount, customerPhone, orderReference, tillNumber, verificationCode]);

  const copyTillNumber = async () => {
    await navigator.clipboard.writeText(tillNumber);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const handleShareOnWhatsApp = () => {
    if (!whatsappTarget || !verificationCode.trim()) {
      return;
    }

    window.open(
      `https://wa.me/${whatsappTarget}?text=${encodeURIComponent(verificationMessage)}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(0,166,80,0.16),_transparent_38%),linear-gradient(180deg,#f6fff9_0%,#ffffff_52%,#f8fafc_100%)]">
      <main className="mx-auto max-w-5xl px-4 py-8 sm:py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-brand-700 hover:text-brand-900">
          <ArrowLeft className="h-4 w-4" />
          Back to store
        </Link>

        <section className="mt-6 overflow-hidden rounded-[32px] border border-safaricom-green/20 bg-white shadow-[0_36px_100px_-52px_rgba(0,166,80,0.65)]">
          <div className="bg-gradient-to-r from-[#061d12] via-[#0a4728] to-safaricom-green px-6 py-8 text-white sm:px-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="max-w-2xl">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-white/12 p-3 ring-1 ring-white/15">
                    <MpesaIcon className="h-8 w-8" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">M-Pesa Till & Verification</h1>
                    <p className="mt-2 max-w-xl text-sm text-white/75 sm:text-base">
                      Use the till below for payment, then share your confirmation code with the team if manual verification is needed.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-white/15 bg-black/20 px-5 py-4">
                <p className="text-xs uppercase tracking-[0.24em] text-white/60">Till Number</p>
                <p className="mt-3 font-mono text-3xl font-bold tracking-[0.34em] text-white">
                  {tillNumber}
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-6 p-6 sm:p-8 lg:grid-cols-[1.1fr_0.9fr]">
            <Card className="border-safaricom-green/15 bg-gradient-to-br from-white to-mpesa-50">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <MpesaIcon className="h-6 w-6" />
                  Payment Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="rounded-2xl border border-safaricom-green/15 bg-slate-950 px-5 py-5 text-white">
                  <p className="text-xs uppercase tracking-[0.24em] text-white/55">Pay To Till</p>
                  <p className="mt-3 font-mono text-4xl font-bold tracking-[0.32em]">{tillNumber}</p>
                  <p className="mt-4 text-sm text-white/70">
                    Business: {settings.business_name || 'Karebe Wines & Spirits'}
                  </p>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button
                    type="button"
                    onClick={() => {
                      void copyTillNumber();
                    }}
                    className="bg-safaricom-green text-white hover:bg-safaricom-dark"
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {copied ? 'Till copied' : 'Copy till number'}
                  </Button>
                  <div className="rounded-2xl border border-mpesa-200 bg-mpesa-50 px-4 py-3 text-sm text-safaricom-dark">
                    <span className="block text-xs uppercase tracking-[0.2em] text-safaricom-dark/70">Amount</span>
                    <span className="mt-1 block text-lg font-semibold">{formatAmount(amount)}</span>
                  </div>
                </div>

                <div className="rounded-2xl border border-mpesa-200 bg-white p-5">
                  <p className="text-sm font-semibold text-safaricom-dark">How to pay</p>
                  <div className="mt-4 space-y-3 text-sm text-gray-700">
                    <p>1. Open M-Pesa on your phone.</p>
                    <p>2. Select <span className="font-medium">Lipa na M-Pesa</span>.</p>
                    <p>3. Choose <span className="font-medium">Buy Goods and Services</span>.</p>
                    <p>4. Enter till number <span className="font-mono font-semibold">{tillNumber}</span>.</p>
                    <p>5. Enter {amount ? <span className="font-medium">{formatAmount(amount)}</span> : 'the correct checkout amount'} and complete payment.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-safaricom-green/15">
              <CardHeader className="pb-3">
                <CardTitle>Manual Verification</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Automatic M-Pesa code checking is not live yet. This page collects the details you can send to the team for manual confirmation.
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">M-Pesa Confirmation Code</label>
                  <Input
                    placeholder="e.g. TGH72K9PLM"
                    value={verificationCode}
                    onChange={(event) => setVerificationCode(event.target.value.toUpperCase())}
                    maxLength={12}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Customer Phone (Optional)</label>
                  <Input
                    placeholder="e.g. 0712 345678"
                    value={customerPhone}
                    onChange={(event) => setCustomerPhone(event.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Order Reference (Optional)</label>
                  <Input
                    placeholder="e.g. KRB-000123"
                    value={orderReference}
                    onChange={(event) => setOrderReference(event.target.value)}
                  />
                </div>

                <Separator />

                <div className="space-y-3">
                  <Button
                    type="button"
                    onClick={handleShareOnWhatsApp}
                    disabled={!whatsappTarget || !verificationCode.trim()}
                    className="w-full bg-[#25D366] text-white hover:bg-[#1fbc57]"
                  >
                    <WhatsAppIcon className="h-4 w-4" />
                    Send code on WhatsApp
                  </Button>

                  {supportPhone ? (
                    <a href={`tel:${supportPhone}`} className="block">
                      <Button type="button" variant="outline" className="w-full">
                        <Phone className="h-4 w-4" />
                        Call {displayPhone || 'support'}
                      </Button>
                    </a>
                  ) : null}
                </div>

                <div className="rounded-2xl border border-brand-100 bg-brand-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-600">
                    Message Preview
                  </p>
                  <pre className="mt-3 whitespace-pre-wrap font-sans text-sm text-brand-900">
                    {verificationMessage}
                  </pre>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
    </div>
  );
}
